/**
 * effects.ts — hitstop and camera shake (AGENTS.md: hitstop and shake are render-time only).
 *
 * Hitstop is a RENDER-time effect only. The server keeps simulating at 20Hz
 * regardless; freezing the client's interpolation clock for 60–90ms makes a
 * connection land with weight without touching the simulation or desynchronising
 * anything. When the freeze ends the clock resumes from where it stopped, so the
 * view stays exactly INTERP_DELAY_MS behind the server rather than skipping.
 */

import * as THREE from 'three';
import { FEEL } from '@dueler/shared';

export interface Effects {
  /** Freezes the render clock for `ms` and shakes the camera by `intensity` (0..1). */
  impact(ms: number, intensity: number, now: number): void;
  /**
   * Converts a real timestamp into render time, holding it still during hitstop.
   * Everything time-based on the client reads this, never performance.now().
   */
  renderTime(now: number): number;
  /** Applies the current shake offset to a camera. Call after the camera is placed. */
  applyShake(camera: THREE.PerspectiveCamera, now: number): void;
  readonly frozen: boolean;
}

export function createEffects(): Effects {
  /** Total ms swallowed by hitstop so far. */
  let held = 0;
  /** When the current freeze ends, in real time. 0 = not frozen. */
  let freezeUntil = 0;
  let freezeFrom = 0;

  let shakeUntil = 0;
  let shakeAmp = 0;
  const offset = new THREE.Vector3();

  return {
    impact(ms, intensity, now) {
      // Overlapping hits extend rather than stack: two hits in one tick should
      // not freeze the world for a fifth of a second.
      const end = now + ms;
      if (end > freezeUntil) {
        if (freezeUntil <= now) freezeFrom = now;
        freezeUntil = end;
      }
      shakeUntil = Math.max(shakeUntil, now + 180);
      shakeAmp = Math.max(shakeAmp, FEEL.CAMERA_SHAKE * Math.min(1, Math.max(0, intensity)));
    },

    renderTime(now) {
      if (now < freezeUntil) {
        // Time stands still: everything interpolating stays on the frame the
        // hit landed on.
        return freezeFrom - held;
      }
      if (freezeUntil > 0) {
        held += freezeUntil - freezeFrom;
        freezeUntil = 0;
      }
      return now - held;
    },

    applyShake(camera, now) {
      if (now >= shakeUntil || shakeAmp <= 0) {
        shakeAmp = 0;
        return;
      }
      const remaining = (shakeUntil - now) / 180;
      const amp = shakeAmp * remaining * remaining;
      // Cheap deterministic-ish jitter; a shake does not need real randomness.
      offset.set(
        Math.sin(now * 0.13) * amp,
        Math.sin(now * 0.21 + 1.7) * amp,
        Math.sin(now * 0.17 + 3.1) * amp,
      );
      camera.position.add(offset);
    },

    get frozen() {
      return freezeUntil > 0;
    },
  };
}
